import { Component, DestroyRef, OnDestroy, computed, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { ClientUiAuthSerive } from '@roosevelt/client-ui-lib';
import { EligUiAuthSerive } from '@roosevelt/elig-ui-lib';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ManageEligibilityMassActionStorePayload, ManageEligibilityMemberStorePayload } from './manage-eligibility.component';
import { IClientPlanDetails, IMassActionState } from '../store/massActions/state';
import { getContractResponse, isFecthingContractInfo } from '../store/massActions/selector';
import { IMemberState } from '../store/member/state';
import { MassActionAsync } from '../store/massActions/async';
import { getSelectedAdminPlan } from '../login/store/selectors';
import { ILoginState } from '../login/store/state';
import { IAppAuthorizations, IUiAuthorizations } from '../authorizations/state';
import { getRelaxedUiAuth, getStrictUiAuth, isFetchingUiAuth } from '../authorizations/selector';

@Component({
  selector: 'app-manage-eligibility',
  template: `
    <app-manage-eligibility-page
      [selectedAdminPlan]="selectedAdminPlan()"
      [manageEligFeatures]="manageEligFeatures()"
      [appUiAuthLoading]="uiAuthLoading()"
      [isFecthingContractInfo]="contractLoading()"
      [contractResponseInput]="contractResponse()"
      [eligStatusReasons]="eligStatusReasons()"
      [qualifierTypes]="qualifierTypes()"
      [memberBenefitTypes]="memberBenefitTypes()"
      (setMassActionStore)="setMassActionStore($event)"
      (setMemberStore)="setMemberStore($event)"></app-manage-eligibility-page>
  `,
  standalone: false
})
export class ManageEligibilityContainer implements OnDestroy {
  readonly luxId = 'BMT-ManageElig-Comp' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);

  readonly relaxedUiAuth = toSignal(this.lux.get(getRelaxedUiAuth, this.luxId), {
    initialValue: {} as IUiAuthorizations | {}
  });
  readonly strictUiAuth = toSignal(this.lux.get(getStrictUiAuth, this.luxId), {
    initialValue: {} as IUiAuthorizations | {}
  });
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly contractResponse = toSignal(this.lux.get(getContractResponse, this.luxId), { initialValue: null as IClientPlanDetails | null });
  readonly uiAuthLoading = toSignal(this.lux.get(isFetchingUiAuth, this.luxId), { initialValue: false });
  readonly contractLoading = toSignal(this.lux.get(isFecthingContractInfo, this.luxId), { initialValue: false });
  readonly manageEligFeatures = computed(() => (this.relaxedUiAuth() as IUiAuthorizations)?.manageeligibility || []);
  readonly eligStatusReasons = computed(() => {
    const contractResponse = this.contractResponse();
    const contract = contractResponse?.client?.subClient?.contract as any;
    const reasons = contract?.eligibilityStatusReasonConfig?.activeSubscriberEligStatusReasonTypes;
    return reasons?.length ? this.createDropDownOptions(reasons) : [];
  });
  readonly qualifierTypes = computed(() => {
    const contractResponse = this.contractResponse();
    const contract = contractResponse?.client?.subClient?.contract as any;
    const qualifierConfig = contract?.eligibilityConfig?.specialAttributeTypes
      ? this.createDropDownOptions(contract.eligibilityConfig.specialAttributeTypes)
      : [];
    return qualifierConfig.filter(qualifier => qualifier.label === 'Retiree' || qualifier.label === 'COBRA');
  });
  readonly memberBenefitTypes = computed(() => {
    const contractResponse = this.contractResponse();
    const contract = contractResponse?.client?.subClient?.contract as any;
    return contract?.benefitsConfig?.memberBenefitTypes
      ? this.createDropDownOptions(contract.benefitsConfig.memberBenefitTypes)
      : [];
  });

  constructor(
    private readonly lux: Lux<{ massAction: IMassActionState, member: IMemberState, login: ILoginState, appUiAuth: IAppAuthorizations }>,
    private readonly msAsync: MassActionAsync,
    private readonly eligUiAuth: EligUiAuthSerive,
    private readonly clientUiAuth: ClientUiAuthSerive,
    private readonly route: Router
  ) {
    effect(() => {
      const uiAuth = this.relaxedUiAuth() as IUiAuthorizations;
      if (uiAuth?.eligibility) {
        this.eligUiAuth.setRelaxedAuthorizations(uiAuth.eligibility);
      }
      if (uiAuth?.client) {
        this.clientUiAuth.setRelaxedAuthorizations(uiAuth.client);
      }
    });

    effect(() => {
      const uiAuth = this.strictUiAuth() as IUiAuthorizations;
      if (uiAuth?.eligibility) {
        this.eligUiAuth.setStrictAuthorizations(uiAuth.eligibility);
      }
      if (uiAuth?.client) {
        this.clientUiAuth.setStrictAuthorizations(uiAuth.client);
      }
    });
  }

  setMassActionStore(storeData: ManageEligibilityMassActionStorePayload): void {
    this.lux.set(state => state.massAction, { ...storeData.massActionState });

    if (storeData.flow === 'Family Enrollment' && storeData.selectedSubClient) {
      const selectedSubClient = storeData.selectedSubClient;
      const contractReqObj = {
        planAcronym: selectedSubClient.planAcronym,
        client: {
          specifiedId: selectedSubClient.client.specifiedId,
        },
        subClient: {
          specifiedId: selectedSubClient.specifiedId,
        },
        asOf: moment(selectedSubClient.asOfDate).format('YYYY-MM-DD'),
        view: [
          'client_basic',
          'client_idCardConfig',
          'client_routeConfig',
          'subclient_basic',
          'subclient_idCardConfig',
          'subclient_memberConfig',
          'contract_basic',
          'contract_benefitsConfig',
          'contract_cobConfig',
          'contract_eligAgeLimitsConfig',
          'contract_eligConfig',
          'contract_restrictionConfig',
          'subclient_routeConfig',
          'contract_eligStatusReasonConfig',
          'client_administrative',
          'rate_qualifier_basic'
        ]
      };
      this.lux.set(state => state.massAction.contractInfo.requestObj, contractReqObj);
      this.msAsync.getContractInfo()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  setMemberStore(storeData: ManageEligibilityMemberStorePayload): void {
    const { event, nextRoute, ...memberState } = storeData;
    this.lux.set(state => state.member, {
      ...memberState
    });

    if (event === 'add flow' && nextRoute) {
      this.route.navigate([nextRoute]);
    }
  }

  private createDropDownOptions(reasons: Array<{ code: string; value: string }> = []): Array<{ value: string; label: string }> {
    return reasons.map(reason => ({
      value: reason.code,
      label: reason.value
    }));
  }


  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }
}
